import {
  Badge,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  type IconName,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  describeDeadline,
  formatYmdLong,
  formatYmdShort,
  type DeadlineTone,
} from "../../lib/order-deadline";

/**
 * Compact bordered trigger so the due field reads as a control, not a 32px
 * meta row. `text-[10px]` beats DatePicker's `text-sm`; a real border beats
 * the transparent wash that made it look unclickable and oversized.
 */
const DEADLINE_PICKER_TRIGGER_GEOMETRY =
  "h-auto min-h-0 w-auto max-w-full shrink-0 gap-1 rounded-sm border-border bg-background px-1.5 py-0.5 text-[10px] font-normal leading-none shadow-none transition-colors duration-150 ease-out [&>span]:min-w-0 [&>span]:flex-none [&_[data-slot=icon]]:size-3 [&_[data-slot=icon]]:text-current";

const DEADLINE_TYPE_CLASS =
  "inline-flex max-w-full items-center gap-1 whitespace-nowrap text-[10px] font-normal leading-none tabular-nums";

export function dueDatePickerTriggerClass(
  tone: DeadlineTone | null | undefined,
): string {
  const fill =
    tone === "overdue"
      ? "border-error/35 bg-error-subtle text-error-subtle-foreground hover:bg-error-subtle/80"
      : tone === "today" || tone === "soon"
        ? "text-warning-subtle-foreground hover:bg-warning-subtle/40"
        : "text-muted-foreground hover:bg-muted/40";
  return cn(DEADLINE_PICKER_TRIGGER_GEOMETRY, fill);
}

export function dueDatePickerIcon(
  tone: DeadlineTone | null | undefined,
): IconName {
  if (tone === "overdue") return "alert-circle";
  if (tone === "today") return "clock";
  return "calendar";
}

function deadlineToneForeground(
  tone: DeadlineTone,
  muted: boolean,
): string {
  if (tone === "overdue") return "text-error-subtle-foreground";
  if (tone === "today") return "text-warning-subtle-foreground";
  if (tone === "soon") {
    return muted
      ? "text-warning/80"
      : "text-warning-subtle-foreground";
  }
  return "text-muted-foreground";
}

interface DeadlineChipProps {
  ymd: string | null;
  todayYmd: string;
  /** The vendor's working weekdays; omitted means calendar days. */
  workingWeekdays?: readonly number[] | null;
  /** Quieter rendering for script rows nested under an order. */
  muted?: boolean;
  className?: string;
}

/**
 * The one deadline signal per row: relative label ("5 days overdue"), absolute
 * on hover ("Expected delivery 23 Aug 2026"). Overdue is the only filled chip;
 * approaching dates are calendar type with an icon so they cannot be mistaken
 * for a status pill. The icon (not colour alone) carries urgency.
 */
export function DeadlineChip({
  ymd,
  todayYmd,
  workingWeekdays,
  muted = false,
  className,
}: DeadlineChipProps) {
  const { t, i18n } = useTranslation();
  const info = describeDeadline(ymd, todayYmd, workingWeekdays);

  if (!info) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center text-[10px] leading-none text-muted-foreground/70",
              className,
            )}
          >
            {t("marketplace.orders.list.deadline.none")}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t("marketplace.orders.list.deadline.noneTooltip")}
        </TooltipContent>
      </Tooltip>
    );
  }

  const absolute = formatYmdLong(info.ymd, i18n.language);
  const label =
    info.tone === "overdue"
      ? t("marketplace.orders.list.deadline.overdue", {
          count: Math.abs(info.daysFromToday),
        })
      : info.tone === "today"
        ? t("marketplace.orders.list.deadline.today")
        : info.tone === "soon"
          ? t("marketplace.orders.list.deadline.soon", {
              count: info.daysFromToday,
            })
          : t("marketplace.orders.list.deadline.scheduled", {
              date: formatYmdShort(info.ymd, todayYmd, i18n.language),
            });

  const showIcon =
    info.tone === "overdue" ||
    info.tone === "today" ||
    (info.tone === "soon" && !muted);
  const iconName = showIcon ? dueDatePickerIcon(info.tone) : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time
          dateTime={info.ymd}
          data-deadline-tone={info.tone}
          className={cn("inline-flex max-w-full", className)}
        >
          {info.tone === "overdue" ? (
            <Badge
              variant="status-error"
              iconStart="alert-circle"
              className="max-w-full tabular-nums"
            >
              {label}
            </Badge>
          ) : (
            <span
              className={cn(
                DEADLINE_TYPE_CLASS,
                deadlineToneForeground(info.tone, muted),
              )}
            >
              {iconName ? (
                <Icon
                  name={iconName}
                  size="xs"
                  color="inherit"
                  className="shrink-0"
                />
              ) : null}
              {label}
            </span>
          )}
        </time>
      </TooltipTrigger>
      <TooltipContent>
        {t("marketplace.orders.list.deadline.tooltip", { date: absolute })}
      </TooltipContent>
    </Tooltip>
  );
}
